import { Product } from '../../products/product.types';
import { EnumValue, EnumExtensions } from '../../common/enum.consts';
import { EventStatus } from '../event.types';
import {
  ActivityType,
  EventOccasion,
  EventType,
  EventVertical
} from '../../meta/meta.types';
import {
  AdjustedItemPrice,
  PagedFilter,
  GeoLocation
} from '../../common/common.types';
import { Moment } from 'moment';

export class ActivityFilter extends PagedFilter {
  eventStatuses?: EventStatus[];
  eventVerticalIds?: number[];
  eventTypesIds?: number[];
  providerIds?: number[];
  statuses?: ActivityStatus[];
  paymentStatuses?: ActivityPaymentStatus[];
  providerSearch?: string;
  conciergeIds?: number[];
  activityBefore?: string | Moment;
  activityAfter?: string | Moment;
}

export class ActivitySummary {
  activityId: number;
  eventId: number;
  extendedEventId: string;

  eventVertical: EventVertical;
  eventType?: EventType;
  eventOccasion?: EventOccasion;

  providerName?: string;
  productName?: string;
  name?: string;
  activityType: ActivityType;

  status: ActivityStatus;
  paymentStatus?: ActivityPaymentStatus;
  canRequestBooking?: boolean;

  startsOn?: Moment | string;
  endsOn?: Moment | string;
  duration?: number;
  isSingleDay: boolean;
  isAllDay: boolean;
  hasDateError?: boolean;
  timezone?: string;

  attendeeTotal?: number;
  currencyCode: string;
  totalCost?: number;

  location?: GeoLocation;
  notes?: string;
}

export class Activity {
  eventId: number;
  activityId: number;
  activityType: ActivityType;
  product?: Product;

  name?: string;
  status: ActivityStatus;
  paymentStatus?: ActivityPaymentStatus;
  isCancelled: boolean;

  startsOn?: Moment | string;
  endsOn?: Moment | string;
  duration?: number;
  isSingleDay: boolean;
  isAllDay: boolean;
  hasDateError?: boolean;
  timezone?: string;

  attendeeTotal?: number;
  attendeeAdjustment?: number;
  isAdminOnly: boolean;

  price?: AdjustedItemPrice;
  cost?: AdjustedItemPrice;

  location?: GeoLocation;
  notes?: string;
}

export enum ActivityStatus {
  Draft = 2,
  NoBookingRequired = 3,
  NeedsBooking = 4,
  BookingRequested = 6,
  NeedsRebooking = 7,
  Booked = 8,
  Complete = 10,
  ToDo = 11,
  RescheduleRequested = 12,
  Postponed = 13,
  CancelRequested = 14,
  ToCancel = 97,
  Declined = 98,
  Cancelled = 99
}

export function ActivityStatusName(status: ActivityStatus): string {
  switch (status) {
    case ActivityStatus.NoBookingRequired:
      return 'No Booking Required';
    case ActivityStatus.NeedsBooking:
      return 'Needs Booking';
    case ActivityStatus.BookingRequested:
      return 'Booking Requested';
    case ActivityStatus.NeedsRebooking:
      return 'Needs Rebooking';
    case ActivityStatus.ToCancel:
      return 'To Cancel';
    case ActivityStatus.ToDo:
      return 'To Do';
    default:
      return ActivityStatus[status];
  }
}

export const ActivityStatusValues: EnumValue<
  ActivityStatus
>[] = EnumExtensions.getMappedNamesAndValues<ActivityStatus>(
  ActivityStatus,
  ActivityStatusName
);

export enum ActivityPaymentStatus {
  DirectDebitExpected = 1,
  AwaitingInvoice = 2,
  InvoiceReceived = 3,
  AwaitingReceipt = 4,
  ReceiptReceived = 5,
  CallToPay = 6,
  EmailToPay = 7,
  Reconciled = 10,
  NotCharged = 99
}

export function ActivityPaymentStatusName(
  status: ActivityPaymentStatus
): string {
  switch (status) {
    case ActivityPaymentStatus.DirectDebitExpected:
      return 'Direct Debit Expected';
    case ActivityPaymentStatus.AwaitingInvoice:
      return 'Awaiting Invoice';
    case ActivityPaymentStatus.InvoiceReceived:
      return 'Invoice Received';
    case ActivityPaymentStatus.AwaitingReceipt:
      return 'Awaiting Receipt';
    case ActivityPaymentStatus.ReceiptReceived:
      return 'Receipt Received';
    case ActivityPaymentStatus.CallToPay:
      return 'Call to Pay';
    case ActivityPaymentStatus.EmailToPay:
      return 'Email to Pay';
    case ActivityPaymentStatus.NotCharged:
      return 'Not Charged';
    default:
      return ActivityPaymentStatus[status];
  }
}

export const ActivityPaymentStatusValues: EnumValue<
  ActivityPaymentStatus
>[] = EnumExtensions.getMappedNamesAndValues<ActivityPaymentStatus>(
  ActivityPaymentStatus,
  ActivityPaymentStatusName
);

export interface UpdatedActivity {
  activityTypeId: number;
  productId?: number;
  name?: string;

  startsOn?: Moment | string;
  endsOn?: Moment | string;
  isAllDay: boolean;
  timezone?: string;

  attendeeAdjustment?: number;
  isAdminOnly: boolean;

  priceAdjustment?: number;
  costAdjustment?: number;
  location?: GeoLocation;
  notes?: string;
}

// tslint:disable-next-line
export interface NewActivity extends UpdatedActivity {}
